// Exchange rates (relative to USD - would be fetched from API in production)
const EXCHANGE_RATES = {
  USD: 1,
  EUR: 0.85,
  GBP: 0.73,
  JPY: 110.5,
  AUD: 1.35,
  CAD: 1.25,
  CHF: 0.92,
  CNY: 6.45,
  INR: 74.5,
  THB: 33.5,
  SGD: 1.35,
  MYR: 4.2
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'Fr',
  CNY: '¥',
  INR: '₹',
  THB: '฿',
  SGD: 'S$',
  MYR: 'RM'
};

// Currency names
const CURRENCY_NAMES = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  JPY: 'Japanese Yen',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  INR: 'Indian Rupee',
  THB: 'Thai Baht',
  SGD: 'Singapore Dollar',
  MYR: 'Malaysian Ringgit'
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (!amount || fromCurrency === toCurrency) {
    return Number(amount);
  }
  
  const fromRate = EXCHANGE_RATES[fromCurrency] || 1;
  const toRate = EXCHANGE_RATES[toCurrency] || 1;
  
  // If converting to same currency, return as-is
  if (fromCurrency === toCurrency) {
    return Number(amount);
  }
  
  // Use precise decimal arithmetic to avoid floating point issues
  const result = (Number(amount) * toRate) / fromRate;
  
  // Fix floating point precision by rounding to 2 decimal places properly
  return Number(result.toFixed(2));
};

/**
 * Format amount with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount with symbol
 */
export const formatCurrency = (amount, currency = 'INR') => {
  const symbol = CURRENCY_SYMBOLS[currency] || '₹';
  const formattedAmount = Number(amount || 0).toFixed(2);
  return `${symbol}${formattedAmount}`;
};

/**
 * Get all available currencies
 * @returns {Array} Array of currency objects
 */
export const getAvailableCurrencies = () => {
  return Object.keys(CURRENCY_SYMBOLS).map(code => ({
    code,
    symbol: CURRENCY_SYMBOLS[code],
    name: CURRENCY_NAMES[code]
  }));
};

/**
 * Convert and format amount in one step
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @returns {string} Formatted converted amount
 */
export const convertAndFormatCurrency = (amount, fromCurrency, toCurrency) => {
  const convertedAmount = convertCurrency(amount, fromCurrency, toCurrency);
  return formatCurrency(convertedAmount, toCurrency);
};

export default {
  convertCurrency,
  formatCurrency,
  getAvailableCurrencies,
  convertAndFormatCurrency
};
